/* Theme-adapted configurable multipack selector. Prices from bundle tags are display-only. */
class PackageBuilder extends HTMLElement {
  connectedCallback() {
    const configElement = this.querySelector('[data-package-builder-config]');
    if (!configElement || this.dataset.initialized === 'true') return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.warn('Package builder configuration could not be read.', error);
      return;
    }

    this.offers = this.getOffers(this.config.tags || []);
    if (!this.offers.length || !this.config.product?.variants?.length) return;

    this.dataset.initialized = 'true';
    this.product = this.config.product;
    this.labels = this.getLabels();
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId || this.product.id;
    this.productRoot = this.closest('.productView') || document;
    if (this.productRoot !== document) this.productRoot.classList.add('package-builder-enabled');
    this.variantSelects = this.productRoot.querySelector(`variant-radios[data-product="${CSS.escape(String(this.productId))}"], variant-selects[data-product="${CSS.escape(String(this.productId))}"]`);
    this.form = document.getElementById(`product-form-${this.productId}`);
    if (!this.form) return;

    this.nativeInputs = this.variantSelects ? [...this.variantSelects.querySelectorAll('.product-form__input')] : [];
    this.activeOffer = this.offers[0];
    this.render();
    this.addEventListener('click', this.onClick.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this), true);
  }

  getOffers(tags) {
    return tags.map((tag) => {
      const match = String(tag).trim().match(/^bundle:(\d+):(\d+(?:\.\d+)?)(?::(\d+))?$/i);
      if (!match) return null;
      return { quantity: Number(match[1]), price: Number(match[2]), selectionCount: Number(match[3] || match[1]) };
    }).filter((offer) => offer && offer.quantity > 0 && offer.selectionCount > 0 && offer.price >= 0)
      .sort((a, b) => a.quantity - b.quantity);
  }

  getLabels() {
    const localeLanguage = String(this.config.locale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const language = localeLanguage;
    const base = {
      package: 'Package', item: 'Item', unit: 'PCS', packageItem: 'Package item', buyNow: 'Buy now',
      chooseAvailable: 'Please choose an available option for every item.', adding: 'Adding…', failed: 'Package could not be added.'
    };
    const translations = {
      ar: { package: 'حزمة', item: 'منتج', unit: 'قطعة', packageItem: 'عنصر الحزمة', buyNow: 'اشترِ الآن' },
      bg: { package: 'Пакет', item: 'Артикул', unit: 'БР.', packageItem: 'Артикул от пакета', buyNow: 'Купи сега' },
      cs: { package: 'Balíček', item: 'Položka', unit: 'KS', packageItem: 'Položka balíčku', buyNow: 'Koupit nyní' },
      da: { package: 'Pakke', item: 'Vare', unit: 'STK.', packageItem: 'Pakkevare', buyNow: 'Køb nu' },
      de: { package: 'Paket', item: 'Artikel', unit: 'STK.', packageItem: 'Paketartikel', buyNow: 'Jetzt kaufen' },
      el: { package: 'Πακέτο', item: 'Προϊόν', unit: 'ΤΕΜ.', packageItem: 'Προϊόν πακέτου', buyNow: 'Αγορά τώρα' },
      es: { package: 'Paquete', item: 'Artículo', unit: 'UDS.', packageItem: 'Artículo del paquete', buyNow: 'Comprar ahora' },
      et: { package: 'Komplekt', item: 'Toode', unit: 'TK', packageItem: 'Komplekti toode', buyNow: 'Osta kohe' },
      fi: { package: 'Paketti', item: 'Tuote', unit: 'KPL', packageItem: 'Paketin tuote', buyNow: 'Osta nyt' },
      fr: { package: 'Lot', item: 'Article', unit: 'PCS', packageItem: 'Article du lot', buyNow: 'Acheter maintenant' },
      hi: { package: 'पैकेज', item: 'आइटम', unit: 'पीस', packageItem: 'पैकेज आइटम', buyNow: 'अभी खरीदें' },
      hr: { package: 'Paket', item: 'Artikl', unit: 'KOM.', packageItem: 'Artikl paketa', buyNow: 'Kupi odmah' },
      hu: { package: 'Csomag', item: 'Tétel', unit: 'DB', packageItem: 'Csomag tétel', buyNow: 'Vásárlás most' },
      id: { package: 'Paket', item: 'Barang', unit: 'PCS', packageItem: 'Barang paket', buyNow: 'Beli sekarang' },
      it: { package: 'Pacchetto', item: 'Articolo', unit: 'PZ.', packageItem: 'Articolo del pacchetto', buyNow: 'Acquista ora' },
      ja: { package: 'セット', item: '商品', unit: '点', packageItem: 'セット商品', buyNow: '今すぐ購入' },
      ko: { package: '세트', item: '상품', unit: '개', packageItem: '세트 상품', buyNow: '지금 구매' },
      lt: { package: 'Rinkinys', item: 'Prekė', unit: 'VNT.', packageItem: 'Rinkinio prekė', buyNow: 'Pirkti dabar' },
      nb: { package: 'Pakke', item: 'Vare', unit: 'STK.', packageItem: 'Pakkevare', buyNow: 'Kjøp nå' },
      nl: { package: 'Pakket', item: 'Artikel', unit: 'ST.', packageItem: 'Pakketartikel', buyNow: 'Nu kopen' },
      pl: { package: 'Pakiet', item: 'Produkt', unit: 'SZT.', packageItem: 'Produkt w pakiecie', buyNow: 'Kup teraz' },
      pt: { package: 'Pacote', item: 'Artigo', unit: 'UN.', packageItem: 'Artigo do pacote', buyNow: 'Comprar agora' },
      ro: { package: 'Pachet', item: 'Articol', unit: 'BUC.', packageItem: 'Articol pachet', buyNow: 'Cumpără acum' },
      ru: { package: 'Комплект', item: 'Товар', unit: 'ШТ.', packageItem: 'Товар комплекта', buyNow: 'Купить сейчас' },
      sk: { package: 'Balíček', item: 'Položka', unit: 'KS', packageItem: 'Položka balíčka', buyNow: 'Kúpiť teraz' },
      sl: { package: 'Paket', item: 'Izdelek', unit: 'KOS', packageItem: 'Izdelek paketa', buyNow: 'Kupi zdaj' },
      sv: { package: 'Paket', item: 'Artikel', unit: 'ST.', packageItem: 'Paketartikel', buyNow: 'Köp nu' },
      th: { package: 'แพ็กเกจ', item: 'สินค้า', unit: 'ชิ้น', packageItem: 'สินค้าในแพ็กเกจ', buyNow: 'ซื้อเลย' },
      tr: { package: 'Paket', item: 'Ürün', unit: 'ADET', packageItem: 'Paket ürünü', buyNow: 'Şimdi satın al' },
      vi: { package: 'Gói', item: 'Sản phẩm', unit: 'CÁI', packageItem: 'Sản phẩm trong gói', buyNow: 'Mua ngay' },
      zh: { package: '套餐', item: '商品', unit: '件', packageItem: '套餐商品', buyNow: '立即购买', chooseAvailable: '请为每件商品选择可售规格。', adding: '正在加入购物车…', failed: '套餐加入购物车失败。' }
    };
    return { ...base, ...(translations[language] || {}) };
  }

  money(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.config.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(value);
    } catch (_) {
      return `${value} ${this.config.currency || ''}`.trim();
    }
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  variantImage(optionIndex, value) {
    const variants = this.product.variants.filter((variant) => variant.options[optionIndex] === value);
    const variant = variants.find((item) => item.available && (item.featured_image || item.image || item.featured_media))
      || variants.find((item) => item.featured_image || item.image || item.featured_media);
    const image = variant?.featured_image || variant?.image || variant?.featured_media?.preview_image;
    if (typeof image === 'string') return image;
    return image?.src || image?.url || '';
  }

  optionUsesImages(optionIndex, values) {
    const images = values.map((value) => this.variantImage(optionIndex, value)).filter(Boolean);
    return images.length === values.length && new Set(images).size > 1;
  }

  offerLabel(offer) {
    return `${offer.quantity} ${this.labels.unit}`;
  }

  get startingVariant() {
    const selectedId = Number(this.form?.querySelector('[name="id"]')?.value);
    return this.product.variants.find((variant) => variant.id === selectedId && variant.available)
      || this.variantSelects?.currentVariant
      || this.product.variants.find((variant) => variant.available)
      || this.product.variants[0];
  }

  render() {
    this.hidden = false;
    this.innerHTML = `
      <fieldset class="package-picker" aria-label="${this.labels.package}">
        <legend class="form__label">${this.labels.package}</legend>
        <div class="package-picker__offers">${this.offers.map((offer) => `
          <button type="button" class="package-card ${offer === this.activeOffer ? 'is-selected' : ''}" data-package-index="${this.offers.indexOf(offer)}" aria-pressed="${offer === this.activeOffer}">
            <span class="package-card__radio" aria-hidden="true"></span>
            <span class="package-card__title">${this.offerLabel(offer)}</span>
            <span class="package-card__price">${this.money(offer.price)}</span>
          </button>`).join('')}</div>
      </fieldset>
      <div class="package-builder__components"></div>`;
    this.setMode();
  }

  setMode() {
    const multipack = this.activeOffer.quantity > 1;
    this.nativeInputs.forEach((input) => input.classList.toggle('package-builder__native-options-hidden', multipack));
    this.variantSelects?.classList.toggle('package-builder__native-options-hidden', multipack);
    this.form.closest('product-form')?.classList.toggle('package-builder-active', multipack);
    this.ensureDirectCheckoutButton(multipack);
    if (multipack) {
      this.renderComponents();
    } else {
      this.querySelector('.package-builder__components').replaceChildren();
    }
  }

  ensureDirectCheckoutButton(show) {
    const group = this.form.querySelector('.productView-group');
    if (!group) return;
    const payment = this.form.querySelector('.productView-payment');
    const host = payment || group;
    let button = this.form.querySelector('.package-builder__buy-now');
    if (!show) {
      button?.remove();
      return;
    }
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'package-builder__buy-now';
      button.textContent = this.labels.buyNow || 'Buy now';
      button.addEventListener('click', () => this.addPackage(true));
      host.appendChild(button);
    }
  }

  renderComponents() {
    const values = this.startingVariant.options || [];
    const options = this.product.options || [];
    this.querySelector('.package-builder__components').innerHTML = Array.from({ length: this.activeOffer.selectionCount }, (_, componentIndex) => `
      <fieldset class="package-builder__component" data-component-index="${componentIndex}">
        <legend class="form__label">${this.labels.item} ${componentIndex + 1}</legend>
        ${options.map((optionName, optionIndex) => {
          const optionValues = [...new Set(this.product.variants.map((variant) => variant.options[optionIndex]))];
          const usesImages = this.optionUsesImages(optionIndex, optionValues);
          return `
            <div class="package-builder__option ${usesImages ? 'package-builder__option--images' : ''}" data-option-index="${optionIndex}">
              <span class="package-builder__option-label">${this.escape(optionName)}</span>
              <div class="package-builder__values">${optionValues.map((value) => {
                const image = usesImages ? this.variantImage(optionIndex, value) : '';
                return `
                  <button type="button" class="package-builder__value ${image ? 'package-builder__value--image' : ''} ${value === values[optionIndex] ? 'is-selected' : ''}" data-value="${this.escape(value)}" aria-label="${this.escape(value)}">
                    ${image ? `<img src="${this.escape(image)}" alt="" width="72" height="72" loading="lazy">` : ''}
                    <span>${this.escape(value)}</span>
                  </button>`;
              }).join('')}</div>
            </div>`;
        }).join('')}
      </fieldset>`).join('');
  }

  onClick(event) {
    const offerButton = event.target.closest('[data-package-index]');
    if (offerButton) {
      this.activeOffer = this.offers[Number(offerButton.dataset.packageIndex)];
      this.render();
      return;
    }
    const valueButton = event.target.closest('.package-builder__value');
    if (valueButton) {
      valueButton.parentElement.querySelectorAll('.package-builder__value').forEach((button) => button.classList.remove('is-selected'));
      valueButton.classList.add('is-selected');
      if (this.activeOffer.quantity === 1) this.syncSingleSelection();
    }
  }

  syncSingleSelection() {
    if (!this.variantSelects) return;
    const component = this.querySelector('.package-builder__component');
    const selections = [...component.querySelectorAll('.package-builder__option')].map((option) => option.querySelector('.package-builder__value.is-selected')?.dataset.value);
    const fieldsets = [...this.variantSelects.querySelectorAll('.product-form__input')];
    let changedControl;

    fieldsets.forEach((fieldset, index) => {
      const select = fieldset.querySelector('select');
      if (select && select.value !== selections[index]) {
        select.value = selections[index];
        changedControl = select;
        return;
      }
      const radio = [...fieldset.querySelectorAll('input[type="radio"]')].find((input) => input.value === selections[index]);
      if (radio && !radio.checked) {
        radio.checked = true;
        changedControl = radio;
      }
    });

    changedControl?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  selectedVariants() {
    return [...this.querySelectorAll('.package-builder__component')].map((component) => {
      const selections = [...component.querySelectorAll('.package-builder__option')].map((option) => option.querySelector('.package-builder__value.is-selected')?.dataset.value);
      return this.product.variants.find((variant) => variant.available && variant.options.every((value, index) => value === selections[index]));
    });
  }

  onSubmit(event) {
    if (this.activeOffer.quantity === 1 || this.adding) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage();
  }

  async addPackage(goToCheckout = false) {
    if (this.adding) return;
    const variants = this.selectedVariants();
    if (variants.some((variant) => !variant)) {
      window.alert(this.labels.chooseAvailable);
      return;
    }

    this.adding = true;
    const submit = this.form.querySelector('[type="submit"]');
    const directCheckout = this.form.querySelector('.package-builder__buy-now');
    const originalText = submit?.querySelector('.single-add-to-cart-button--text')?.textContent;
    if (submit) {
      submit.setAttribute('aria-disabled', 'true');
      submit.classList.add('loading');
      const text = submit.querySelector('.single-add-to-cart-button--text');
      if (text) text.textContent = this.labels.adding;
    }
    if (directCheckout) directCheckout.setAttribute('aria-disabled', 'true');

    const packageId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const items = variants.map((variant, index) => ({
      id: variant.id,
      quantity: 1,
      properties: {
        [this.labels.package]: this.offerLabel(this.activeOffer),
        [this.labels.packageItem]: `${index + 1} / ${this.activeOffer.selectionCount}`,
        _package_id: packageId
      }
    }));

    try {
      const response = await fetch(this.config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || this.labels.failed);
      window.location.assign(goToCheckout ? '/checkout' : (this.config.cartUrl || '/cart'));
    } catch (error) {
      console.error(error);
      window.alert(error.message || this.labels.failed);
    } finally {
      this.adding = false;
      if (submit) {
        submit.removeAttribute('aria-disabled');
        submit.classList.remove('loading');
        const text = submit.querySelector('.single-add-to-cart-button--text');
        if (text && originalText) text.textContent = originalText.trim();
      }
      directCheckout?.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('package-builder')) customElements.define('package-builder', PackageBuilder);
